///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

/**
 * Types of measurements that may need unit conversion
 */
export enum MeasurementType {
	/** Distance measurements (e.g., between cities, travel distance) */
	DISTANCE = 'distance',
	/** Altitude/elevation measurements (e.g., city elevation, mountain height) */
	ALTITUDE = 'altitude',
	/** Weight measurements (e.g., mass, weight) */
	WEIGHT = 'weight',
	/** Temperature measurements */
	TEMPERATURE = 'temperature',
	/** Speed measurements (e.g., wind speed, vehicle speed) */
	SPEED = 'speed',
	/** Volume measurements */
	VOLUME = 'volume',
	/** Area measurements */
	AREA = 'area'
}

/**
 * Unit system types
 */
export enum UnitSystem {
	METRIC = 'metric',
	US_CUSTOMARY = 'us_customary'
}

/**
 * Detects and caches the browser locale at module initialization
 */
function detectBrowserLocale(): string {
	if(typeof navigator === 'undefined') {
		return 'en' // Default fallback for SSR
	}
	return navigator.language || navigator.languages?.[0] || 'en'
}

/**
 * Extracts country code from locale string
 */
function getCountryCode(locale: string): string {
	return locale.toLowerCase().split('-')[1] || ''
}

/**
 * Calculates unit system for a measurement type based on country code
 */
function calculateUnitSystem(measurementType: MeasurementType, countryCode: string): UnitSystem {
	// Countries/regions that use US customary units for altitude
	const altitudeImperialCountries = ['us', 'pr', 'vi', 'gu', 'as', 'ca', 'gb', 'ie']

	// Countries/regions that use US customary units for most measurements
	const usCustomaryCountries = ['us', 'pr', 'vi', 'gu', 'as', 'lr', 'mm'] // US, territories, Liberia, Myanmar

	// Countries that use Fahrenheit
	const fahrenheitCountries = ['us', 'pr', 'vi', 'gu', 'as', 'bz', 'pw']

	switch(measurementType) {
		case MeasurementType.ALTITUDE:
			// Altitude: US, Canada, UK territories use feet
			return altitudeImperialCountries.includes(countryCode)
				? UnitSystem.US_CUSTOMARY
				: UnitSystem.METRIC

		case MeasurementType.DISTANCE:
		case MeasurementType.SPEED:
		case MeasurementType.AREA:
		case MeasurementType.VOLUME:
			// Distance, speed, area, volume: US and a few others use US customary
			return usCustomaryCountries.includes(countryCode)
				? UnitSystem.US_CUSTOMARY
				: UnitSystem.METRIC

		case MeasurementType.WEIGHT:
			// Weight: US and a few others use pounds
			return usCustomaryCountries.includes(countryCode)
				? UnitSystem.US_CUSTOMARY
				: UnitSystem.METRIC

		case MeasurementType.TEMPERATURE:
			// Temperature: US, Belize, Palau use Fahrenheit
			return fahrenheitCountries.includes(countryCode)
				? UnitSystem.US_CUSTOMARY
				: UnitSystem.METRIC

		default:
			// Default to metric for unknown measurement types
			return UnitSystem.METRIC
	}
}

// Cache: Pre-calculate unit systems for all measurement types at module initialization
const browserLocale = detectBrowserLocale()
const countryCode = getCountryCode(browserLocale)
const unitSystemCache = new Map<MeasurementType, UnitSystem>()

// Initialize cache for all measurement types
Object.values(MeasurementType).forEach(type => {
	unitSystemCache.set(type, calculateUnitSystem(type, countryCode))
})

/**
 * Determines the appropriate unit system based on browser locale and measurement type.
 * Uses cached values for efficiency (locale doesn't change during session).
 *
 * Rules:
 * - Altitude: US, Canada, UK territories use feet (US customary)
 * - Distance: US uses miles/feet (US customary), most others use km/m (metric)
 * - Weight: US uses pounds (US customary), most others use kg (metric)
 * - Temperature: US uses Fahrenheit (US customary), most others use Celsius (metric)
 * - Speed: US uses mph (US customary), most others use km/h (metric)
 *
 * @param measurementType - The type of measurement being displayed
 * @param locale - Optional locale string (overrides cached browser locale if provided)
 * @returns The unit system to use (METRIC or US_CUSTOMARY)
 */
export function getUnitSystem(measurementType: MeasurementType, locale?: string): UnitSystem {
	// If locale is provided, calculate on-the-fly (for testing/override)
	if(locale) {
		const overrideCountryCode = getCountryCode(locale)
		return calculateUnitSystem(measurementType, overrideCountryCode)
	}

	// Use cached value (much faster)
	return unitSystemCache.get(measurementType) ?? UnitSystem.METRIC
}

/**
 * Convenience function to check if metric units should be used
 * @param measurementType - The type of measurement being displayed
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns true if metric units should be used, false for US customary
 */
export function shouldUseMetric(measurementType: MeasurementType, locale?: string): boolean {
	return getUnitSystem(measurementType, locale) === UnitSystem.METRIC
}

/**
 * Convenience function to check if US customary units should be used
 * @param measurementType - The type of measurement being displayed
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns true if US customary units should be used, false for metric
 */
export function shouldUseUSCustomary(measurementType: MeasurementType, locale?: string): boolean {
	return getUnitSystem(measurementType, locale) === UnitSystem.US_CUSTOMARY
}
